import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Briefcase, Plus, Sparkles } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreationStudio = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectType, setProjectType] = useState('website');
  const [description, setDescription] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: getAuthHeaders()
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!projectName.trim()) return;

    try {
      await axios.post(
        `${API}/projects`,
        { name: projectName, type: projectType, description },
        { headers: getAuthHeaders() }
      );
      toast.success('Project created successfully');
      setShowCreate(false);
      setProjectName('');
      setDescription('');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  const handleGenerateBrand = async (projectId) => {
    toast.info('Generating brand identity...');
    try {
      await axios.post(
        `${API}/projects/${projectId}/brand`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Brand identity generated!');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to generate brand');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="text-muted-foreground mt-4">Loading projects...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="creation-studio-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-5xl font-bold text-white mb-3">CREATION STUDIO</h1>
            <p className="text-muted-foreground text-lg">Build digital infrastructure autonomously</p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="new-project-button"
            className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold px-8 py-4 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Project
          </button>
        </div>

        {/* Hero Section */}
        <div className="glass-card p-12 border border-white/10" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1765767056681-9583b29007cf?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>
          <div className="bg-black/80 backdrop-blur-md p-12 -m-12">
            <h2 className="text-3xl font-bold text-white mb-4">Full-Spectrum Creation Engine</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mb-8">
              Design websites, build applications, generate brand identities, create marketing campaigns,
              produce media assets, and deploy finished digital infrastructure with minimal human input.
            </p>
            <div className="flex gap-4">
              <div className="bg-secondary/50 px-6 py-3 border border-border">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Websites</p>
              </div>
              <div className="bg-secondary/50 px-6 py-3 border border-border">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Applications</p>
              </div>
              <div className="bg-secondary/50 px-6 py-3 border border-border">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Brand Identity</p>
              </div>
              <div className="bg-secondary/50 px-6 py-3 border border-border">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Marketing</p>
              </div>
            </div>
          </div>
        </div>

        {/* Projects */}
        <div>
          <h2 className="text-2xl font-bold text-white mb-6 uppercase tracking-tight">Your Projects</h2>
          
          {projects.length === 0 ? (
            <div className="glass-card p-12 border border-white/10 text-center">
              <Briefcase className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
              <h3 className="text-xl font-bold text-white mb-3">No Projects Yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Start building digital infrastructure with AI-powered creation
              </p>
              <button
                onClick={() => setShowCreate(true)}
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold px-8 py-4"
              >
                Create First Project
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => (
                <div key={project.id} className="glass-card p-8 border border-white/10 hover:border-primary/50 transition-colors" data-testid={`project-${project.id}`}>
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">{project.name}</h3>
                      <span className="text-xs uppercase tracking-widest text-muted-foreground px-3 py-1 bg-secondary/50">
                        {project.type}
                      </span>
                    </div>
                  </div>
                  
                  {project.description && (
                    <p className="text-muted-foreground mb-6 text-sm">{project.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between">
                    <span className={`text-xs uppercase tracking-widest px-3 py-1 ${
                      project.status === 'planning' ? 'bg-secondary text-muted-foreground' :
                      project.status === 'brand_generated' ? 'bg-primary/20 text-primary' :
                      'bg-green-500/20 text-green-500'
                    }`}>
                      {project.status}
                    </span>
                    
                    {project.status === 'planning' && (
                      <button
                        onClick={() => handleGenerateBrand(project.id)}
                        data-testid={`generate-brand-${project.id}`}
                        className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary hover:text-primary/80 transition-colors"
                      >
                        <Sparkles className="w-4 h-4" />
                        Generate Brand
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create Project Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
            <div className="glass-card p-8 border border-white/10 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()} data-testid="create-project-modal">
              <h3 className="text-2xl font-bold text-white mb-6 uppercase tracking-tight">Create Project</h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-3">Project Name</label>
                  <input
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="On Point Jar"
                    data-testid="project-name-input"
                    className="w-full bg-secondary/50 border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-6 py-4 text-white"
                  />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-3">Project Type</label>
                  <select
                    value={projectType}
                    onChange={(e) => setProjectType(e.target.value)}
                    data-testid="project-type-select"
                    className="w-full bg-secondary/50 border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-6 py-4 text-white"
                  >
                    <option value="website">Website</option>
                    <option value="web-app">Web Application</option>
                    <option value="mobile-app">Mobile App</option>
                    <option value="brand-identity">Brand Identity</option>
                    <option value="marketing-campaign">Marketing Campaign</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-3">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe your project..."
                    rows="4"
                    data-testid="project-description-input"
                    className="w-full bg-secondary/50 border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-6 py-4 text-white resize-none"
                  />
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={handleCreateProject}
                    data-testid="confirm-create-project"
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold px-8 py-4"
                  >
                    Create
                  </button>
                  <button
                    onClick={() => setShowCreate(false)}
                    data-testid="cancel-create-project"
                    className="flex-1 bg-secondary text-secondary-foreground hover:bg-secondary/80 uppercase tracking-widest text-xs font-bold px-8 py-4"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CreationStudio;
